<template>
    <dialog-template
        headerTitle="会员日折扣设置"
        :visible="visible"
        :width="'480px'"
        @close="close"
        :append-to-body="true">
        <div class="item-wrapper">
            <span class="item-left">频率：</span>
            <div class="item-right">
                <el-radio v-model="forms.frequency"
                    class="mr-20"
                    :label="frequency.week"/>
                <el-radio v-model="forms.frequency" :label="frequency.month"/>
            </div>
        </div>
        <div class="item-wrapper">
            <span class="item-left">时间：</span>
            <div class="item-right">
                <week-picker v-if="forms.frequency === frequency.week"
                    :weekCache.sync="forms.weekDate"/>
                <date-picker v-if="forms.frequency === frequency.month"
                    :dateCache.sync="forms.monthDate"/>
            </div>
        </div>
        <div class="item-wrapper">
            <span class="item-left">折扣对象：</span>
            <div class="item-right">
                <el-radio v-model="forms.discountTarget"
                    class="mr-20"
                    :label="discountTarget.hasBalance"/>
                <el-radio v-model="forms.discountTarget" :label="discountTarget.all"/>
            </div>
        </div>
        <div v-if="forms.discountTarget === discountTarget.hasBalance"
            class="switch-wrapper fx-v-center mt-20">
            <span class="item-left mr-20">余额不足仍享受折扣</span>
            <el-switch v-model="forms.isLowBalanceDiscount"
                active-color="#009688"
                inactive-color="#ddd"/>
        </div>
        <div class="item-wrapper mt-20">
            <span class="item-left">折扣力度：</span>
            <div class="item-right">
                <el-input v-model="forms.discountDepth"
                    placeholder="请输入0～10之间的数字，最多两位小数"
                    @input="discountIptLimit(forms, 'discountDepth')">
                    <span class="color-333"
                        slot="suffix">折</span>
                </el-input>
            </div>
        </div>
        <div class="footer fx-h-center" slot="footer">
            <el-button
                type="primary"
                size="medium"
                :disabled="!canConfirm"
                @click="confirmClick">
                确认
            </el-button>
            <el-button type="default"
                size="medium"
                @click="close">取消</el-button>
        </div>
    </dialog-template>
</template>

<script lang='ts'>
import { createComponent, ref, SetupContext, computed, watch } from '@vue/composition-api'
import SideContainer from '@/components/layout/side-container-style2.vue'
import DatePicker from './date_picker.vue'
import WeekPicker from './week_picker.vue'
import { inputLimit, fixNumPro } from '@/utils'
import { IMemberDiscountActivity } from './member_day_table.vue'
import $fetch from '../../../../api'
import { openMessage } from '../../../../utils/system'

enum Frequency {
    week = '每周',
    month = '每月'
}

enum DiscountTarget {
    hasBalance = '限有余额会员',
    all = '所有会员'
}

interface IForms {
    frequency: Frequency,
    weekDate: number[],
    monthDate: number[],
    discountTarget: DiscountTarget,
    isLowBalanceDiscount: boolean,
    discountDepth: number | string
}

export default createComponent({
    props: {
        visible: {
            type: Boolean,
            required: true
        },
        mode: {
            type: String,
            required: true
        },
        discountActivity: {
            type: Object,
            default: {}
        },
        confirmCallback: {
            type: Function
        }
    },

    setup(
        props: {
            visible: boolean,
            mode: string,
            discountActivity: IMemberDiscountActivity,
            confirmCallback?: () => void
        },
        ctx: SetupContext
    ) {
        const isLoading = ref(false)
        const isConfirmLoading = ref(false)
        const forms = ref<IForms>({
            frequency: Frequency.week,
            weekDate: [],
            monthDate: [],
            discountTarget: DiscountTarget.hasBalance,
            isLowBalanceDiscount: false,
            discountDepth: ''
        })

        const frequencyMap = {
            0: 'week',
            1: 'month',
            每周: 0,
            每月: 1
        }
        const discountObjectMap = {
            0: 'hasBalance',
            1: 'all',
            限有余额会员: 0,
            所有会员: 1
        }
        const discountIptLimit = inputLimit(/^\d(\.\d{0,2})?$/)

        watch(() => props.visible, (newVal: boolean) => {
            if (newVal) {
                init()
            }
        })

        const canConfirm = computed(() => {
            const oForms = forms.value
            if (frequencyMap[oForms.frequency] === 0) {
                return oForms.weekDate.length > 0 && (+oForms.discountDepth)
            } else {
                return oForms.monthDate.length > 0 && (+oForms.discountDepth)
            }
        })

        const headerTitle = computed(() => {
            switch (props.mode) {
                case 'create':
                    return '设置折扣'
                case 'update':
                    return '编辑折扣'
                default:
                    return '未知标题'
            }
        })

        function init() {
            switch (props.mode) {
                case 'create':
                    forms.value = {
                        frequency: Frequency.week,
                        weekDate: [],
                        monthDate: [],
                        discountTarget: DiscountTarget.hasBalance,
                        isLowBalanceDiscount: false,
                        discountDepth: ''
                    }
                    break
                case 'update':
                    const discountActivity = props.discountActivity
                    forms.value = {
                        frequency: Frequency[frequencyMap[discountActivity.frequency]],
                        weekDate: discountActivity.weekdays,
                        monthDate: discountActivity.monthdays,
                        discountTarget: DiscountTarget[discountObjectMap[discountActivity.discount_object]],
                        isLowBalanceDiscount: !discountActivity.sufficient_balance_required,
                        discountDepth: fixNumPro(discountActivity.discount_rate * 10)
                    }
                    break
                default:
                    break
            }
        }

        function close() {
            ctx.emit('update:visible', false)
        }

        function fixDiscount(d) {
            const res = (+d / 10).toString().split('.')
            const l = res[0]
            const r = res[1].slice(0, 3)

            return res[1] ? +`${l}.${r}` : +l
        }

        function confirmClick() {
            updateMemberDiscount()
        }

        async function updateMemberDiscount() {
            try {
                const oForms = forms.value
                isConfirmLoading.value = true
                await $fetch.post({
                    url: '/activity/updatememberdiscount',
                    params: {
                        weekdays: frequencyMap[oForms.frequency] === 0 ? oForms.weekDate : undefined,
                        monthdays: frequencyMap[oForms.frequency] === 1 ? oForms.monthDate : undefined,
                        discount_object: discountObjectMap[oForms.discountTarget],
                        sufficient_balance_required: !oForms.isLowBalanceDiscount,
                        discount_rate: fixNumPro(+oForms.discountDepth / 10, 3)
                    }
                })
                close()
                props.confirmCallback && props.confirmCallback()
                openMessage('success', '更新成功')
            } catch (e) {
                openMessage('error', e.message || '更新失败')
            } finally {
                isConfirmLoading.value = false
            }
        }

        return {
            // data
            isLoading,
            isConfirmLoading,
            forms,
            frequency: Frequency,
            discountTarget: DiscountTarget,
            // computed
            canConfirm,
            headerTitle,
            // methods
            confirmClick,
            discountIptLimit,
            close
        }
    },
    components: {
        SideContainer,
        DatePicker,
        WeekPicker
    }
})
</script>

<style lang="scss" scoped>
.item-wrapper {
    display: flex;
    align-items: center;
    
    & + .item-wrapper {
        margin-top: 20px;
    }

    .item-left {
        flex: 0 0 80px;
    }

    .item-right {
        flex: 1;
    }
}
.body {
    padding: 0 12px;
    font-size: 14px;
    color: #333;

    ::v-deep .el-input {
        &__inner {
            padding: 0 8px;
        }
    }

    .item-wrapper {
        display: flex;
        align-items: center;
        margin-top: 20px;

        .item-left {
            flex: 0 0 100px;
        }

        .item-right {
            flex: 1;
        }
    }

    .switch-wrapper {
        height: 20px;

        .item-left {
            flex: none;
        }
    }
}

.footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 100%;
    height: 100%;
    padding: 7px 12px;
}
</style>

